import { createSocketAuth } from '@/lib/socketAuth'
import { useState, useEffect, useRef, useCallback } from 'react'
import { callApi, type IncomingCall, type ActiveCall, type CallLog, type ConnectionStatus } from '@/api/callApi'
import { mapIncomingCall } from '@/lib/callPayload'
import { toast } from 'sonner'
import i18n from '@/i18n'

const WS_BASE = import.meta.env.VITE_WS_BASE ?? ''
// First reconnect fires in 500ms so a dropped connection during an incoming call
// doesn't create a multi-second blind window.
const RECONNECT_BASE_DELAY = 500
const RECONNECT_MAX_DELAY = 30000
// 连续失败达此次数（指数退避 ~2 分钟）后降为慢速重试：服务端持续拒绝
// （如账号被停用——Clerk 仍能签新 token，握手永远 403）时，30s 一次的
// 无限循环会打满后端日志。慢速模式保住长时间断网后的自愈能力。
const MAX_FAST_RECONNECT_ATTEMPTS = 10
const SLOW_RECONNECT_INTERVAL = 5 * 60_000
// call_ws_manager.py's _fan_out only caches `incoming_call` in Redis for
// offline redelivery (_cache_pending/_flush_pending) — a call_status
// (missed/completed/voicemail) broadcast while this socket happens to be
// disconnected is silently and permanently dropped, with no replay on
// reconnect. An unexpected network disconnect risks its
// end-of-call status update landing in exactly that gap, leaving it stuck at
// its last-known status (e.g. permanently "ringing") in the shared
// activeCalls store forever. Reconciling against GET /calls/active (the
// server's authoritative ringing/answered list) on every reconnect recovers
// from that loss: entries missing from the fresh snapshot are dropped
// (stale/ended); entries present are replaced with server truth. `sinceIso`
// guards the one race this must not "fix" away: a WS event that adds a call
// AFTER the reseed request was issued but before its response lands — such
// an entry legitimately isn't in the (already-in-flight) snapshot yet, so it
// survives if its createdAt is at or after the reseed's issue time.
// `removedSids` guards the mirror race of `sinceIso`: a WS event that REMOVES
// (call_status completed/missed/voicemail) or upgrades a call after the reseed
// request was issued but before its response lands. The snapshot predates that
// event, so without this it would resurrect the ended call (or roll an
// answered call back to ringing) until the next state reconciliation.
// Fresh entries also lack guestName/listingName (ActiveCallResponse doesn't
// carry them) — keep the richer local copy rather than washing it to null.
function reconcileActiveCalls(
  prev: CallLog[],
  fresh: CallLog[],
  sinceIso: string,
  removedSids: ReadonlySet<string>,
): CallLog[] {
  const prevBySid = new Map(prev.map((c) => [c.callSid, c]))
  const merged = fresh
    .filter((c) => !removedSids.has(c.callSid))
    .map((c) => {
      const local = prevBySid.get(c.callSid)
      if (!local) return c
      return {
        ...c,
        guestName: c.guestName ?? local.guestName,
        listingName: c.listingName ?? local.listingName,
        // Never downgrade answered → ringing off a snapshot taken before the claim.
        ...(local.status === 'answered' && c.status === 'ringing'
          ? { status: local.status, answeredBy: local.answeredBy, answeredByName: local.answeredByName }
          : {}),
      }
    })
  const freshSids = new Set(fresh.map((c) => c.callSid))
  const addedSinceReseed = prev.filter((c) => !freshSids.has(c.callSid) && c.createdAt >= sinceIso)
  return [...merged, ...addedSinceReseed]
}

// How many concluded callSids to remember. A tombstone only has to outlive the
// window in which a stale replay can still arrive — the server's PENDING_TTL
// (30s) plus a reconnect — so this is generously sized for even the busiest
// tenant and bounded so it can never grow without limit in a long-lived tab.
const CONCLUDED_CALL_MEMORY = 200

// Calls that have stopped ringing (claimed by someone, or ended outright) and
// must never re-open a ringing popup if a stale `incoming_call` is replayed for
// them. Backed by a Map purely for its insertion order — oldest entry is evicted
// once the cap is reached.
//
// Why the client needs its own memory: `incomingCalls` only ever REMOVES
// entries, so before this it had no way to tell "a call is ringing" from "a call
// I already saw conclude is being announced again". The server caches
// `incoming_call` for offline agents (call_ws_manager's _cache_pending) and
// replays it on reconnect — and this client deliberately reconnects every
// recovery after network loss. Any gap in the server's cache
// invalidation therefore surfaces here as a fully clickable popup for a dead
// call. The server-side gaps are fixed, but this is the independent guardrail:
// event ORDER across a reconnect is not something the client can assume.
function rememberConcluded(store: Map<string, true>, callSid: string) {
  store.delete(callSid)
  store.set(callSid, true)
  while (store.size > CONCLUDED_CALL_MEMORY) {
    const oldest = store.keys().next()
    if (oldest.done) break
    store.delete(oldest.value)
  }
}

// Softphone readiness, reported separately from the WebSocket's own status.
// 'registered'   — Device.register() resolved; calls can actually reach this browser.
// 'unregistered' — register() threw, or the Device raised an error afterwards.
//                  Previously both cases were swallowed (empty catch / bare
//                  console.error) so the agent had no way to know.
// 'unknown'      — no attempt has resolved yet, or the call system is disabled.
export type DeviceStatus = 'registered' | 'unregistered' | 'unknown'

// Twilio Voice SDK error codes that genuinely mean "this Device is no longer
// registered and cannot receive calls". Values verified against the installed
// SDK's own generated error classes (@twilio/voice-sdk/es5/twilio/errors/
// generated.js), not from memory:
//
//   20101 AuthorizationErrors.AccessTokenInvalid
//   20104 AuthorizationErrors.AccessTokenExpired
//   20151 AuthorizationErrors.AuthenticationFailed
//   31005 GeneralErrors.ConnectionError      — signaling connection lost
//   31009 GeneralErrors.TransportError       — transport lost
//   31204 AuthorizationErrors.InvalidJWTTokenError
//   31205 AuthorizationErrors.JWTTokenExpiredError
//
// NOT included, despite being commonly assumed to be registration failures:
//   31401 UserMediaErrors.PermissionDeniedError
//   31402 UserMediaErrors.AcquisitionFailedError
// Those are MICROPHONE problems. The Device stays registered and still receives
// calls; the agent just has no mic. Flipping status on them would produce exactly
// the false "disconnected" toasts this narrowing exists to remove.
//
// Also excluded as per-call/transient: 31002 ConnectionDeclined, 31003
// ConnectionTimeout, 31008 CallCancelled, 31000 UnknownError. Fix A/B re-verify
// real Device.state on every reconnect and every interval tick, so a genuine
// loss hiding behind one of these is corrected within ~75s regardless.
export const REGISTRATION_LOSS_CODES: ReadonlySet<number> = new Set([
  20101, 20104, 20151, 31005, 31009, 31204, 31205,
])

// Backstop cadence for the Device self-check (Fix B). Deliberately a plain
// setInterval: browsers throttle timers in background tabs but do not stop them,
// and drift happening while nobody is looking is the entire scenario this covers.
// Nothing here is gated on document.visibilityState.
const DEVICE_CHECK_INTERVAL_MS = 75_000

// Subset of REGISTRATION_LOSS_CODES that means "the Access Token is bad", as
// opposed to transport loss. The SDK re-registers by itself after a transport
// drop (Device._shouldReRegister), but it cannot mint a new token — for these we
// fetch one and re-register straight away instead of waiting for the grace timer.
const TOKEN_ERROR_CODES: ReadonlySet<number> = new Set([20101, 20104, 20151, 31204, 31205])
// A backend that keeps signing bad tokens would otherwise turn error → refresh →
// register → error into a tight loop.
const TOKEN_RECOVERY_MIN_INTERVAL_MS = 30_000

// How long a lost Device may stay down before the agent is told. Most losses
// (laptop lid, Wi-Fi switch, token expiry) heal within seconds — toasting on the
// first error left a "disconnected" banner on screen for a Device that was
// already fine again. 30s of no audio is still well under a ring timeout.
const DEVICE_LOSS_GRACE_MS = 30_000
// Fixed toast id: one banner per outage, updated in place and dismissed as soon
// as the Device registers again.
const DEVICE_NOTICE_TOAST_ID = 'softphone-device-status'
// Ask Twilio for tokenWillExpire this long before expiry (SDK default 10s leaves
// no room for a slow /calls/token round-trip).
const TWILIO_TOKEN_REFRESH_MS = 30_000
// On tab-visible / back-online, refresh the token early if it expires within this.
const TOKEN_NEAR_EXPIRY_MS = 5 * 60_000
// Device.register() does not reject while signaling is unreachable — it waits
// for the transport to come back (seen in a real-browser walkthrough: pending
// for the whole outage). Without a cap, one hung attempt held the self-heal lock
// and the grace timer awaited it forever, so the banner never appeared.
const REGISTER_ATTEMPT_TIMEOUT_MS = 15_000
// The grace timer's last-chance re-register gets at most this long before the
// banner decision is made regardless.
const GRACE_FINAL_CHECK_MS = 5_000

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    p.then(
      (v) => { clearTimeout(id); resolve(v) },
      (e) => { clearTimeout(id); reject(e) },
    )
  })
}

export interface UseCallSocketReturn {
  incomingCalls: IncomingCall[]
  activeCall: ActiveCall | null
  activeCalls: CallLog[]
  dnd: boolean
  connectionStatus: ConnectionStatus
  // Twilio Device readiness — DISTINCT from connectionStatus (the WebSocket).
  // The two drift apart: the WS can be alive and delivering incoming_call events
  // while the softphone never registered, which is exactly how an agent claimed
  // three calls she could not receive on 2026-08-10. 'unknown' until the first
  // register() attempt resolves.
  deviceStatus: DeviceStatus
  answerCall: (callSid: string) => Promise<void>
  declineCall: (callSid: string) => void
  endCall: (callSid?: string) => Promise<void>
  holdCall: (callSid: string) => Promise<void>
  unholdCall: (callSid: string) => Promise<void>
  toggleDnd: () => Promise<void>
}

// enabled=false（用户无 call:read，或租户未配置电话系统）时整套停机：不连 WS、
// 不初始化 Twilio Device、不发 /calls/active、/staff/dnd 种子请求。翻转为 true
// 时（权限/租户探测异步就绪）effect 重跑并正常启动。
export function useCallSocket(enabled = true): UseCallSocketReturn {
  const [incomingCalls, setIncomingCalls] = useState<IncomingCall[]>([])
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null)
  const [activeCalls, setActiveCalls] = useState<CallLog[]>([])
  const [dnd, setDnd] = useState(false)
  const dndRef = useRef(false)
  const updateDnd = useCallback((value: boolean) => { dndRef.current = value; setDnd(value) }, [])
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected')

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tokenRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const socketAuthRef = useRef<ReturnType<typeof createSocketAuth> | null>(null)
  // callSids removed (call ended) or otherwise mutated by WS AFTER the most
  // recent /calls/active reseed was issued — see reconcileActiveCalls. Reset
  // each time a reseed request goes out.
  const removedSinceReseedRef = useRef<Set<string>>(new Set())
  const incomingSinceReseedRef = useRef<Set<string>>(new Set())
  const reseedInFlightRef = useRef(false)
  const reseedAgainRef = useRef(false)
  const reseedRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reseedAttemptsRef = useRef(0)
  const reseedAbortRef = useRef<AbortController | null>(null)
  const dismissedSinceReseedRef = useRef<Set<string>>(new Set())
  // GET /calls/active returned 403 (no call:read permission). Deterministic per
  // login — stop reissuing it on every reconnect; transient
  // failures (network / 5xx) keep retrying so the resync isn't lost.
  const activeCallsForbiddenRef = useRef(false)
  // callSids that have stopped ringing — see rememberConcluded above.
  const concludedCallsRef = useRef<Map<string, true>>(new Map())
  const twilioDeviceRef = useRef<any>(null)
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>('unknown')
  // Mirror of deviceStatus for use inside callbacks that must not re-create on
  // every status change (answerCall, the heartbeat interval) — reading state
  // directly there would capture a stale value from the closure.
  const deviceStatusRef = useRef<DeviceStatus>('unknown')
  // Grace window before a Device loss is announced — see DEVICE_LOSS_GRACE_MS.
  const deviceLossGenerationRef = useRef(0)
  const lastWsActivityRef = useRef(Date.now())
  const resumeInFlightRef = useRef<Promise<void> | null>(null)
  const deviceLossTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const deviceNoticeShownRef = useRef(false)
  const deviceNoticeKeyRef = useRef<'disconnected' | 'registerFailed'>('disconnected')
  const lastTokenRecoveryAtRef = useRef(0)
  const twilioTokenExpiresAtRef = useRef(0)
  const tokenRefreshInFlightRef = useRef<Promise<void> | null>(null)
  const tokenRefreshAbortRef = useRef<AbortController | null>(null)
  const initInFlightRef = useRef(false)
  const ensureInFlightRef = useRef<Promise<void> | null>(null)
  const resetDeviceNotice = useCallback(() => {
    deviceLossGenerationRef.current += 1
    if (deviceLossTimerRef.current) clearTimeout(deviceLossTimerRef.current)
    deviceLossTimerRef.current = null
    if (deviceNoticeShownRef.current) toast.dismiss(DEVICE_NOTICE_TOAST_ID)
    deviceNoticeShownRef.current = false
  }, [])
  const markDevice = useCallback((s: DeviceStatus) => {
    deviceStatusRef.current = s
    setDeviceStatus(s)
    if (s === 'registered') resetDeviceNotice()
  }, [resetDeviceNotice])
  const twilioCallRef = useRef<any>(null)
  const mountedRef = useRef(true)
  // Sequence token for toggleDnd: only the result of the MOST RECENT setDnd
  // request is ever applied. Guards against rapid double-clicks whose two
  // overlapping requests could resolve out of order and leave `dnd` stuck on
  // the older (now stale) response.
  const dndRequestSeqRef = useRef(0)
  // auth:unauthorized（统一登出）后置 true：彻底停止重连。登出后 Clerk
  // 已 signOut，再重连只会永远 403。
  const stoppedRef = useRef(false)
  // Incremented on every intentional close (cleanup / StrictMode unmount).
  // Each connect() captures the epoch at call time; onclose only schedules a
  // reconnect when the epoch still matches — i.e. the close was unexpected.
  const epochRef = useRef(0)

  // Stable ref to break circular dependency between connect ↔ scheduleReconnect
  const connectFnRef = useRef<(isReconnect?: boolean) => Promise<void>>(async () => {})
  const refreshTokenFnRef = useRef<() => Promise<void>>(async () => {})

  // Dispose the per-connection authentication lifecycle.
  // Called on every close (intentional or not) and in the effect cleanup.
  const clearWsTimers = useCallback(() => {
    socketAuthRef.current?.dispose()
    socketAuthRef.current = null
  }, [])

  // Re-seed activeCalls from GET /calls/active — the server's authoritative
  // ringing/answered list. Used on every reconnect (see reconcileActiveCalls)
  // and to repair local state after a failed answer.
  const resyncActiveCalls = useCallback((): void => {
    if (activeCallsForbiddenRef.current || stoppedRef.current || !mountedRef.current || !navigator.onLine) return
    if (reseedInFlightRef.current) { reseedAgainRef.current = true; return }
    if (reseedRetryRef.current) clearTimeout(reseedRetryRef.current)
    reseedRetryRef.current = null
    reseedInFlightRef.current = true
    const controller = new AbortController()
    reseedAbortRef.current = controller
    const epoch = epochRef.current
    const reseedIssuedAt = new Date().toISOString()
    removedSinceReseedRef.current = new Set()
    incomingSinceReseedRef.current = new Set()
    dismissedSinceReseedRef.current = new Set()
    void callApi.getActiveCalls(controller.signal).then((res) => {
      if (!mountedRef.current || stoppedRef.current || epoch !== epochRef.current) return
      reseedAttemptsRef.current = 0
      const removed = removedSinceReseedRef.current
      setActiveCalls((prev) =>
        reconcileActiveCalls(prev, res.calls, reseedIssuedAt, removed),
      )
      const ringing = new Set(res.calls.filter(c => c.status === 'ringing').map(c => c.callSid))
      const newIncoming = incomingSinceReseedRef.current
      const dismissed = new Set([...dismissedSinceReseedRef.current, ...concludedCallsRef.current.keys()])
      setIncomingCalls(prev => {
        // Old servers can clear stale popups; only a routing-aware snapshot can
        // recover missing ones. Events received during this request take priority.
        const recovered = res.incoming ?? prev.filter(c => ringing.has(c.callSid))
        const existing = new Set(prev.map(c => c.callSid))
        const calls = new Map(recovered.filter(c => !dismissed.has(c.callSid) && (!dndRef.current || existing.has(c.callSid))).map(c => [c.callSid, c]))
        for (const call of prev) {
          if (newIncoming.has(call.callSid) && !dismissed.has(call.callSid)) calls.set(call.callSid, call)
        }
        return [...calls.values()]
      })
    }).catch((err) => {
      if (controller.signal.aborted || !mountedRef.current || stoppedRef.current || epoch !== epochRef.current) return
      if ((err as { response?: { status?: number } })?.response?.status === 403) {
        activeCallsForbiddenRef.current = true
        return
      }
      const attempt = reseedAttemptsRef.current++
      const delay = attempt >= 6 ? SLOW_RECONNECT_INTERVAL : Math.min(30_000, 1000 * 2 ** attempt)
      reseedRetryRef.current = setTimeout(resyncActiveCalls, delay + Math.random() * 250)
    }).finally(() => {
      reseedInFlightRef.current = false
      reseedAbortRef.current = null
      if (reseedAgainRef.current) {
        reseedAgainRef.current = false
        if (mountedRef.current && !stoppedRef.current) resyncActiveCalls()
      }
    })
  }, [])

  const scheduleReconnect = useCallback(() => {
    if (!WS_BASE || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
    // 快速阶段用指数退避；连续失败太多次说明服务端在持续拒绝（而非网络
    // 抖动），降为 5 分钟慢速重试
    const delay =
      reconnectAttemptRef.current >= MAX_FAST_RECONNECT_ATTEMPTS
        ? SLOW_RECONNECT_INTERVAL
        : Math.min(
            RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttemptRef.current),
            RECONNECT_MAX_DELAY,
          )
    reconnectAttemptRef.current += 1
    // Only actual connection loss schedules a reconnect and state resync.
    reconnectTimerRef.current = setTimeout(() => { void connectFnRef.current(true) }, delay)
  }, [])

  const connect = useCallback(async (isReconnect = false) => {
    if (!WS_BASE || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
    if (wsRef.current?.readyState === WebSocket.OPEN) return
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return

    // Ignore callbacks from a connection that was intentionally disposed.
    const epoch = epochRef.current

    setConnectionStatus('connecting')

    try {
      const ws = new WebSocket(`${WS_BASE}/ws/calls`)
      wsRef.current = ws
      lastWsActivityRef.current = Date.now()

      const auth = createSocketAuth(ws, () => {
        if (epoch !== epochRef.current || !mountedRef.current) return
        setConnectionStatus('connected')
        reconnectAttemptRef.current = 0

        // Resync after a real disconnect; in-session renewal never runs this callback again.
        if (isReconnect) {
          resyncActiveCalls()
        }

        // Connection recovery is also a device health checkpoint.
        void ensureDeviceRef.current('ws-open')

      })
      socketAuthRef.current = auth
      ws.onopen = () => { void auth.open() }

      ws.onmessage = (event) => {
        if (!mountedRef.current || stoppedRef.current || epoch !== epochRef.current) return
        try {
          lastWsActivityRef.current = Date.now()
          // Backend sends flat snake_case fields — no nested `data` wrapper.
          // Payload shapes (from call_ws_manager.py):
          //   incoming_call : { event, call_sid, caller_number, call_log_id, guest_id,
          //                     guest_name, reservation_id, listing_name, target_user_ids,
          //                     dnd }  ← per-recipient flag spliced in by call_ws_manager.py's
          //                             _fan_out/_flush_pending; not part of the backend's
          //                             IncomingCallEvent Pydantic model, added at send time
          //   call_claimed  : { event, call_sid, answered_by_user_id, answered_by_name, target_user_ids }
          //   call_hold_changed : { event, call_sid, on_hold } ← tenant-wide broadcast
          //                   (no target_user_ids gate), same as call_claimed; only the
          //                   agent whose activeCall.callSid matches actually reacts.
          //   call_status   : { event, call_sid, status, target_user_ids }
          //                   status: missed | completed | voicemail (broadcast to all
          //                   targets) | declined (targeted ONLY at the declining user)
          //   ping          : { event }
          const msg = JSON.parse(event.data as string) as { event: string; type?: string; [key: string]: any }
          if (auth.handle(msg)) return
          switch (msg.event) {
            case 'resync':
              resyncActiveCalls()
              break
            case 'ping':
              // Only pong on the active socket — epoch check is appropriate here
              // because a duplicate pong from a stale socket is harmless but noisy.
              if (ws.readyState === WebSocket.OPEN && epoch === epochRef.current) {
                ws.send(JSON.stringify({ event: 'pong', deviceStatus: deviceStatusRef.current }))
              }
              break
            case 'incoming_call': {
              if (reseedInFlightRef.current) incomingSinceReseedRef.current.add(msg.call_sid as string)
              dismissedSinceReseedRef.current.delete(msg.call_sid as string)
              // A call we've already seen conclude cannot start ringing again:
              // this is a stale replay out of the server's offline cache
              // (typically flushed on a token-refresh reconnect). Dropping it
              // here keeps a dead call from re-opening a clickable popup.
              // Deliberately NOT applied to 'declined' — declining is a personal
              // pass, the call keeps ringing, and a ring-level escalation can
              // legitimately re-target the same agent with the same call_sid.
              if (concludedCallsRef.current.has(msg.call_sid as string)) {
                console.warn(
                  '[useCallSocket] ignoring replayed incoming_call for concluded call',
                  msg.call_sid,
                )
                break
              }
              const call = mapIncomingCall(msg)

              // Always update the shared active-calls store (drives the persistent
              // call-status icon) regardless of this user's DND state — DND only
              // gates the popup below, never the tenant-wide active-calls view.
              setActiveCalls((prev) => {
                if (prev.some((c) => c.callSid === call.callSid)) return prev
                return [
                  ...prev,
                  {
                    id: String(call.callLogId),
                    callSid: call.callSid,
                    callerNumber: call.callerNumber,
                    guestName: call.guestName ?? null,
                    listingName: call.listingName ?? null,
                    answeredBy: null,
                    answeredByName: null,
                    duration: null,
                    status: 'ringing',
                    isResolved: false,
                    createdAt: new Date().toISOString(),
                    onHold: false,
                    // A call that is still ringing has no post-call analysis yet.
                    topics: [],
                  },
                ]
              })

              // DND suppresses only the popup — the call still rings for this
              // user via Twilio and still shows in the persistent icon above.
              if (!(msg.dnd as boolean | undefined)) {
                setIncomingCalls((prev) => {
                  if (prev.some((c) => c.callSid === call.callSid)) return prev
                  return [...prev, call]
                })
              }
              break
            }
            case 'call_claimed':
              // Someone has the call — it is no longer ringing for anyone, so a
              // later replayed incoming_call for it must not re-open a popup.
              rememberConcluded(concludedCallsRef.current, msg.call_sid as string)
              // Another agent answered: mark the popup claimed and show
              // "Answered by [name]" for a few seconds, then auto-dismiss.
              setIncomingCalls((prev) =>
                prev.map((c) =>
                  c.callSid === msg.call_sid
                    ? {
                        ...c,
                        claimedBy: msg.answered_by_user_id as string,
                        claimedByName: (msg.answered_by_name as string | null) ?? null,
                      }
                    : c,
                ),
              )
              setActiveCalls((prev) =>
                prev.map((c) =>
                  c.callSid === msg.call_sid
                    ? {
                        ...c,
                        status: 'answered',
                        answeredBy: msg.answered_by_user_id as string,
                        answeredByName: (msg.answered_by_name as string | null) ?? null,
                      }
                    : c,
                ),
              )
              setTimeout(() => {
                if (!mountedRef.current) return
                setIncomingCalls((prev) =>
                  prev.filter((c) => c.callSid !== msg.call_sid),
                )
              }, 3000)
              break
            case 'claim_voided': {
              // The server released this agent's claim because the dial to their
              // browser client never connected, and re-queued the caller. Their
              // UI set "connected" the moment POST /calls/answer returned and has
              // been lying ever since (2026-08-10, CA101840cc). Correct it.
              const sid = msg.call_sid as string
              setActiveCall((prev) => (prev && prev.callSid === sid ? null : prev))
              setIncomingCalls((prev) => prev.filter((c) => c.callSid !== sid))
              setActiveCalls((prev) =>
                prev.map((c) =>
                  c.callSid === sid
                    ? { ...c, status: 'ringing', answeredBy: null, answeredByName: null }
                    : c,
                ),
              )
              // Explain it rather than silently reverting — an in-call UI vanishing
              // with no reason reads as a frontend bug to the agent.
              toast.error(i18n.t('callRouting:device.claimVoided'))
              break
            }

            case 'call_hold_changed':
              setActiveCall((prev) =>
                prev && prev.callSid === msg.call_sid ? { ...prev, onHold: msg.on_hold as boolean } : prev,
              )
              break
            case 'call_status':
              // 'declined' is targeted ONLY at the declining user (the backend does
              // not broadcast it), so removing here dismisses just this user's popup
              // — never everyone's. missed/completed/voicemail are broadcast to all
              // targets (the call is over for all of them).
              // ai_handled/forwarded 是 AI 接待/转接的终态(2026-07-31 call_log 状态扩宽),
              // 不收进来会导致弹窗常驻、active-call 不清。
              if (['missed', 'completed', 'voicemail', 'declined', 'ai_handled', 'forwarded'].includes(msg.status as string)) {
                if (reseedInFlightRef.current) dismissedSinceReseedRef.current.add(msg.call_sid as string)
                setIncomingCalls((prev) =>
                  prev.filter((c) => c.callSid !== msg.call_sid),
                )
                setActiveCall((prev) =>
                  prev?.callSid === msg.call_sid ? null : prev,
                )
              }
              // The call itself is only actually over (for every agent, not just
              // the one who declined) on missed/completed/voicemail — remove it
              // from the shared active-calls store in those cases only.
              if (['missed', 'completed', 'voicemail', 'ai_handled', 'forwarded'].includes(msg.status as string)) {
                if (reseedInFlightRef.current) removedSinceReseedRef.current.add(msg.call_sid as string)
                // Terminal: remember it so a stale replay can't resurrect it.
                rememberConcluded(concludedCallsRef.current, msg.call_sid as string)
                setActiveCalls((prev) => prev.filter((c) => c.callSid !== msg.call_sid))
              }
              break
          }
        } catch (err) {
          // A dropped message is a functional failure (e.g. a missed incoming_call
          // means an agent never sees a ringing call) — never swallow silently.
          console.warn('[useCallSocket] failed to handle WS message', err)
        }
      }

      ws.onclose = () => {
        auth.dispose()
        // epoch mismatch means this close was triggered intentionally (cleanup,
        // StrictMode test-unmount, or logout). Don't reconnect.
        if (epoch !== epochRef.current) return
        if (!mountedRef.current) return
        setConnectionStatus('disconnected')
        scheduleReconnect()
      }

      ws.onerror = () => {
        if (epoch !== epochRef.current || !mountedRef.current) return
        setConnectionStatus('error')
      }
    } catch {
      if (epoch !== epochRef.current || !mountedRef.current) return
      setConnectionStatus('error')
      scheduleReconnect()
    }
  }, [scheduleReconnect, resyncActiveCalls])

  // Keep ref current so scheduleReconnect can call without stale closure
  connectFnRef.current = connect

  // Backstop schedule for the Twilio token. The primary trigger is the SDK's own
  // tokenWillExpire event; this timer only matters if that event is missed.
  const scheduleTwilioTokenRefresh = useCallback((expiresAt: string) => {
    twilioTokenExpiresAtRef.current = new Date(expiresAt).getTime()
    if (tokenRefreshTimerRef.current) clearTimeout(tokenRefreshTimerRef.current)
    tokenRefreshTimerRef.current = null
    const msUntilRefresh = twilioTokenExpiresAtRef.current - Date.now() - 60_000
    if (msUntilRefresh > 0 && mountedRef.current) {
      tokenRefreshTimerRef.current = setTimeout(() => {
        void refreshTokenFnRef.current()
      }, msUntilRefresh)
    }
  }, [])

  const refreshTwilioToken = useCallback(async () => {
    if (!WS_BASE || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
    if (tokenRefreshInFlightRef.current) return tokenRefreshInFlightRef.current
    const controller = new AbortController()
    tokenRefreshAbortRef.current = controller
    const pending = (async () => {
      try {
        const resp = await callApi.getToken(controller.signal)
        if (controller.signal.aborted || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
        const device = twilioDeviceRef.current
        if (device && device.state !== 'destroyed') device.updateToken(resp.token)
        scheduleTwilioTokenRefresh(resp.expiresAt)
      } catch (err) {
        if (!controller.signal.aborted && navigator.onLine && !stoppedRef.current) {
          console.warn('[useCallSocket] Twilio token refresh failed; device may stop receiving calls', err)
        }
      }
    })().finally(() => {
      if (tokenRefreshInFlightRef.current === pending) tokenRefreshInFlightRef.current = null
      if (tokenRefreshAbortRef.current === controller) tokenRefreshAbortRef.current = null
    })
    tokenRefreshInFlightRef.current = pending
    return pending
  }, [scheduleTwilioTokenRefresh])

  refreshTokenFnRef.current = refreshTwilioToken

  // Record a Device loss. The status flips immediately (heartbeat + Answer
  // warning read it), but the agent is only TOLD if the Device is still down
  // after DEVICE_LOSS_GRACE_MS — and only once per outage. Recovery (any
  // markDevice('registered')) cancels the timer and dismisses the banner.
  const reportDeviceLoss = useCallback((key: 'disconnected' | 'registerFailed') => {
    markDevice('unregistered')
    if (!WS_BASE || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
    if (deviceNoticeShownRef.current || deviceLossTimerRef.current) return
    deviceNoticeKeyRef.current = key
    const generation = deviceLossGenerationRef.current
    const dueAt = Date.now() + DEVICE_LOSS_GRACE_MS
    deviceLossTimerRef.current = setTimeout(() => {
      if (generation !== deviceLossGenerationRef.current) return
      if (Date.now() - dueAt > 10_000) {
        // Timer was suspended with the machine. Give recovery a fresh grace period.
        deviceLossTimerRef.current = null
        reportDeviceLoss(key)
        return
      }
      void (async () => {
        // Last attempt with a fresh token before interrupting the agent —
        // capped, because a register() against dead signaling never settles.
        await Promise.race([
          ensureDeviceRef.current('grace'),
          new Promise((r) => setTimeout(r, GRACE_FINAL_CHECK_MS)),
        ])
        // Cleared by markDevice('registered') or by unmount while we awaited.
        if (generation !== deviceLossGenerationRef.current || !deviceLossTimerRef.current || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
        deviceLossTimerRef.current = null
        if (deviceStatusRef.current === 'registered') return
        deviceNoticeShownRef.current = true
        toast.error(i18n.t(`callRouting:device.${deviceNoticeKeyRef.current}`), {
          id: DEVICE_NOTICE_TOAST_ID,
          // Stays up for the whole outage; markDevice('registered') takes it down.
          duration: Infinity,
        })
      })()
    }, DEVICE_LOSS_GRACE_MS)
  }, [markDevice])

  const initTwilioDevice = useCallback(async () => {
    if (initInFlightRef.current || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
    initInFlightRef.current = true
    const epoch = epochRef.current
    let device: any = null
    try {
      const { Device } = await import('@twilio/voice-sdk')
      const resp = await callApi.getToken()
      if (!mountedRef.current || stoppedRef.current || !navigator.onLine || epoch !== epochRef.current) return

      device = new Device(resp.token, {
        logLevel: 0,
        // @ts-expect-error -- codec option typing varies by SDK version
        codecPreferences: ['opus', 'pcmu'],
        tokenRefreshMs: TWILIO_TOKEN_REFRESH_MS,
        // maxCallSignalingTimeoutMs deliberately NOT set: per the SDK docs a
        // non-zero value makes Twilio hold the call open for that long after
        // signaling drops, delaying the status callbacks that drive
        // missed/completed on the server.
      })
      // Published before register() so a Device whose FIRST register fails is
      // still reachable by the self-heal paths — previously the ref was only set
      // on success, so ensureDeviceRegistered saw no Device and never retried.
      twilioDeviceRef.current = device
      scheduleTwilioTokenRefresh(resp.expiresAt)

      device.on('error', (err: unknown) => {
        const code = (err as { code?: number })?.code
        console.error('[Twilio] Device error:', code, err)
        // Only genuine registration loss flips the status. Everything else
        // (mic permission, per-call decline/timeout) is logged and left alone —
        // the periodic self-check re-verifies real Device.state anyway, so a
        // misclassification here self-corrects instead of nagging the agent.
        if (typeof code !== 'number' || !REGISTRATION_LOSS_CODES.has(code)) return
        if (twilioDeviceRef.current !== device) return // a superseded Device
        reportDeviceLoss('disconnected')
        // Transport loss: the SDK re-registers on its own when signaling comes
        // back. A bad token it cannot fix — get a new one and re-register now.
        if (TOKEN_ERROR_CODES.has(code)) {
          const now = Date.now()
          if (now - lastTokenRecoveryAtRef.current >= TOKEN_RECOVERY_MIN_INTERVAL_MS) {
            lastTokenRecoveryAtRef.current = now
            // Refresh unconditionally: Device.state may still read 'registered'
            // at this point, and ensureDeviceRegistered would trust it and keep
            // the dead token.
            void (async () => {
              await refreshTokenFnRef.current()
              await ensureDeviceRef.current('token-error')
            })()
          }
        }
      })

      // Twilio's recommended refresh trigger; the scheduled timer is the backstop.
      device.on('tokenWillExpire', () => {
        void refreshTokenFnRef.current()
      })

      // Covers the SDK's own re-register after a signaling drop, which none of
      // our code paths would otherwise observe.
      device.on('registered', () => {
        if (twilioDeviceRef.current === device && mountedRef.current && !stoppedRef.current && navigator.onLine) markDevice('registered')
      })
      // The SDK goes 'unregistered' when signaling drops, often well before any
      // error event (walkthrough: ~14s, and only once our own probe hit it).
      // Start the grace clock at the real moment of loss.
      device.on('unregistered', () => {
        if (twilioDeviceRef.current === device && mountedRef.current) reportDeviceLoss('disconnected')
      })

      device.on('incoming', (call: any) => {
        twilioCallRef.current = call
        try { call.accept() } catch {}
      })

      const firstAttempt: Promise<void> = device.register()
      firstAttempt.catch(() => {}) // late failure after the cap: already reported
      await withTimeout(firstAttempt, REGISTER_ATTEMPT_TIMEOUT_MS, 'Device.register()')
      // Superseded (unmount, or a StrictMode remount started another init).
      if (!mountedRef.current || stoppedRef.current || twilioDeviceRef.current !== device) {
        try { device.destroy() } catch {}
        return
      }
      if (!navigator.onLine) return
      markDevice('registered')
    } catch (err) {
      if (device && twilioDeviceRef.current !== device) return
      // WAS an empty catch with only a comment. Swallowing this is what let an
      // agent sit in the ring group looking available while her softphone had
      // never registered — she claimed three calls she could not receive
      // (2026-08-10). Signalling really does still work over WS + REST, which is
      // why this isn't fatal, but the agent has to be told they cannot take audio.
      console.error('[Twilio] Device registration failed:', err)
      if (!mountedRef.current) return
      reportDeviceLoss('registerFailed')
    } finally {
      initInFlightRef.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Single self-heal path shared by the WS-reconnect hook (Fix A), the periodic
  // backstop (Fix B), tab-visible/back-online, token errors and the loss grace
  // timer. Reads the Device's OWN state rather than our mirrored deviceStatus —
  // the whole failure mode is those two disagreeing.
  //
  // Silent on success by design: an agent must not be toasted every 75s just
  // because the connection layer is doing its job. A failed attempt goes
  // through reportDeviceLoss, which only speaks up if the Device stays down.
  const ensureDeviceRegistered = useCallback(async (trigger: string) => {
    if (!WS_BASE || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
    // Several triggers can land together (WS open + visibility + interval);
    // a second register() mid-flight would throw InvalidStateError.
    if (ensureInFlightRef.current) return ensureInFlightRef.current
    const run = async () => {
      const device = twilioDeviceRef.current
      if (!device) { await initTwilioDevice(); return }
      const state = device.state as string | undefined
      if (state === 'registered') {
        // Correct our mirror if it had drifted pessimistic (e.g. a transient error
        // we no longer downgrade on, or a recovery we never observed).
        if (deviceStatusRef.current !== 'registered') markDevice('registered')
        return
      }
      if (state === 'destroyed') return // nothing to re-register; a fresh init owns this
      // SDK is already re-registering (its own reconnect, or init) — let it finish.
      if (state === 'registering') return
      try {
        console.info(`[Twilio] re-registering Device (trigger=${trigger}, state=${state})`)
        // The Device may be down precisely because its token expired (laptop
        // slept past the refresh timer); re-registering with it again would
        // just fail again. Always go in with a fresh one.
        if (trigger !== 'token-error') await refreshTokenFnRef.current() // token-error just did
        if (twilioDeviceRef.current !== device || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
        if (device.state !== 'unregistered') {
          if (device.state === 'registered') markDevice('registered')
          return
        }
        const attempt: Promise<void> = device.register()
        // If the cap fires first, the attempt may still settle later: success
        // arrives via the 'registered' event, failure must not go unhandled.
        attempt.catch(() => {})
        await withTimeout(attempt, REGISTER_ATTEMPT_TIMEOUT_MS, 'Device.register()')
        if (twilioDeviceRef.current !== device || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
        markDevice('registered')
      } catch (err) {
        console.error(`[Twilio] Device re-registration failed (trigger=${trigger}):`, err)
        if (twilioDeviceRef.current !== device || !mountedRef.current || stoppedRef.current || !navigator.onLine) return
        // The grace timer calls us itself — don't let it re-arm itself.
        if (trigger === 'grace') markDevice('unregistered')
        else reportDeviceLoss('registerFailed')
      }
    }
    const p = run().finally(() => { ensureInFlightRef.current = null })
    ensureInFlightRef.current = p
    return p
  }, [markDevice, reportDeviceLoss, initTwilioDevice])

  // Keep the latest closure reachable from the WS handler and the interval
  // without either needing to be re-created when it changes.
  const ensureDeviceRef = useRef(ensureDeviceRegistered)
  useEffect(() => { ensureDeviceRef.current = ensureDeviceRegistered }, [ensureDeviceRegistered])

  // Coming back from sleep / a hidden tab / offline is exactly when the Device
  // and its token are most likely stale — check now rather than at the next
  // 75s tick. Refresh the token first if it is (nearly) expired: timers don't
  // run while the machine sleeps.
  useEffect(() => {
    if (!enabled || !WS_BASE) return
    const pause = () => {
      tokenRefreshAbortRef.current?.abort()
      tokenRefreshInFlightRef.current = null
      resetDeviceNotice()
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
      epochRef.current += 1
      reseedAbortRef.current?.abort()
      if (reseedRetryRef.current) clearTimeout(reseedRetryRef.current)
      reseedRetryRef.current = null
      clearWsTimers()
      wsRef.current?.close()
      wsRef.current = null
      setConnectionStatus('disconnected')
      markDevice('unregistered')
    }
    const recheck = (event: Event) => {
      if ((document.visibilityState === 'hidden' && event.type !== 'online') || !navigator.onLine || stoppedRef.current || !mountedRef.current) return
      if (resumeInFlightRef.current) return
      resetDeviceNotice()
      // A socket that looked OPEN before sleep may already be dead at the server.
      if (wsRef.current && Date.now() - lastWsActivityRef.current > 45_000) {
        epochRef.current += 1
        reseedAbortRef.current?.abort()
        if (reseedRetryRef.current) clearTimeout(reseedRetryRef.current)
        reseedRetryRef.current = null
        clearWsTimers()
        wsRef.current.close()
        wsRef.current = null
      }
      if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
        reconnectAttemptRef.current = 0
        void connectFnRef.current(true)
      }
      const nearExpiry = twilioTokenExpiresAtRef.current - Date.now() < TOKEN_NEAR_EXPIRY_MS
      const recovery = (async () => {
        if (nearExpiry && twilioDeviceRef.current?.state === 'registered') await refreshTokenFnRef.current()
        await ensureDeviceRef.current('resume')
        if (deviceStatusRef.current !== 'registered') reportDeviceLoss('disconnected')
      })().finally(() => { if (resumeInFlightRef.current === recovery) resumeInFlightRef.current = null })
      resumeInFlightRef.current = recovery
    }
    document.addEventListener('visibilitychange', recheck)
    window.addEventListener('online', recheck)
    window.addEventListener('offline', pause)
    return () => {
      document.removeEventListener('visibilitychange', recheck)
      window.removeEventListener('online', recheck)
      window.removeEventListener('offline', pause)
    }
  }, [enabled, clearWsTimers, markDevice, reportDeviceLoss, resetDeviceNotice])

  // Fix B — backstop for the case Fix A cannot see: the WS is perfectly healthy
  // (so it never reconnects, so nothing re-checks) while the Device has quietly
  // dropped. Deliberately NOT gated on document.visibilityState or focus: the
  // drift this exists to catch happens precisely in idle background tabs, and an
  // agent still shows as available in the ring group while backgrounded.
  useEffect(() => {
    if (!enabled) return
    const id = setInterval(() => {
      void ensureDeviceRef.current('periodic')
    }, DEVICE_CHECK_INTERVAL_MS)
    return () => clearInterval(id)
  }, [enabled])

  const answerCall = useCallback(async (callSid: string) => {
    const epoch = epochRef.current
    // Advisory WARNING, deliberately not a block. Three reasons it must not
    // return early:
    //   1. The Device can drop between this check and Twilio's dial, so blocking
    //      buys no guarantee — the server-side agent_dial_failed recovery is the
    //      real safety net.
    //   2. Twilio being unavailable entirely is a supported degraded mode ("call
    //      signalling still works via WS + REST API"); refusing to answer there
    //      would turn a partial outage into a total one.
    //   3. A false negative (Device actually fine, state stale) would lock an
    //      agent out of every call — strictly worse than the bug being mitigated.
    // So: tell them, then let them try.
    if (deviceStatusRef.current === 'unregistered') {
      toast.error(i18n.t('callRouting:device.cannotAnswer'))
    }
    try {
      const result = await callApi.answerCall(callSid)
      if (!mountedRef.current || stoppedRef.current || epoch !== epochRef.current) return
      rememberConcluded(concludedCallsRef.current, callSid)
      setIncomingCalls((prev) => prev.filter((c) => c.callSid !== callSid))
      setActiveCalls(prev => prev.map(c => c.callSid === callSid ? { ...c, status: 'answered' } : c))
      // result's declared ActiveCall type doesn't match the backend's actual
      // POST /calls/answer response shape (a pre-existing mismatch, out of
      // scope here) — onHold is set explicitly rather than trusted from result.
      setActiveCall({ ...result, onHold: false })

      if (twilioCallRef.current) {
        try { twilioCallRef.current.accept() } catch {}
      }
    } catch {
      if (!mountedRef.current || stoppedRef.current || epoch !== epochRef.current) return
      // The answer failed — most commonly because the caller hung up before the
      // agent clicked Answer, so the Twilio call is no longer in-progress. Dismiss
      // the stale popup regardless so it doesn't linger for a call that's already gone.
      setIncomingCalls((prev) => prev.filter((c) => c.callSid !== callSid))

      // Repair activeCalls rather than leaving this call stuck. Two steps,
      // and the order matters: first drop any local 'answered' marking for
      // THIS call, because reconcileActiveCalls deliberately refuses to
      // downgrade answered → ringing (it can't tell a stale snapshot from a
      // wrong local state), so a resync alone would preserve exactly the bad
      // value we're trying to clear. Then resync, which supplies the truth —
      // the entry disappears if the call is gone, or comes back correctly
      // attributed if another agent actually holds it.
      setActiveCalls((prev) =>
        prev.map((c) =>
          c.callSid === callSid && c.status === 'answered'
            ? { ...c, status: 'ringing', answeredBy: null, answeredByName: null }
            : c,
        ),
      )
      resyncActiveCalls()

      toast.error(i18n.t('callRouting:callStatus.answerFailed'))
    }
  }, [resyncActiveCalls])

  const declineCall = useCallback((callSid: string) => {
    const epoch = epochRef.current
    // Decline only this user's invitation; other recipients keep ringing.
    // The HTTP acknowledgement also dismisses locally if the WS frame was lost.
    void callApi.declineCall(callSid).then(() => {
      if (!mountedRef.current || stoppedRef.current || epoch !== epochRef.current) return
      if (reseedInFlightRef.current) dismissedSinceReseedRef.current.add(callSid)
      setIncomingCalls(prev => prev.filter(c => c.callSid !== callSid))
    }).catch((err) => {
      console.warn('[useCallSocket] decline call failed', err)
    })
    if (twilioCallRef.current) {
      try {
        twilioCallRef.current.reject()
        twilioCallRef.current = null
      } catch {}
    }
  }, [])

  const endCall = useCallback(async (callSid?: string) => {
    setActiveCall((prev) => {
      const sid = callSid ?? prev?.callSid
      if (sid) { void callApi.endCall(sid).catch(() => {}) }
      return null
    })
    if (twilioCallRef.current) {
      try {
        twilioCallRef.current.disconnect()
        twilioCallRef.current = null
      } catch {}
    }
  }, [])

  const holdCall = useCallback(async (callSid: string) => {
    try {
      await callApi.holdCall(callSid)
      setActiveCall((prev) => (prev && prev.callSid === callSid ? { ...prev, onHold: true } : prev))
    } catch (err) {
      console.warn('[useCallSocket] holdCall failed', err)
      toast.error(i18n.t('callRouting:callStatus.holdFailed'))
    }
  }, [])

  const unholdCall = useCallback(async (callSid: string) => {
    try {
      await callApi.unholdCall(callSid)
      setActiveCall((prev) => (prev && prev.callSid === callSid ? { ...prev, onHold: false } : prev))
    } catch (err) {
      console.warn('[useCallSocket] unholdCall failed', err)
      toast.error(i18n.t('callRouting:callStatus.unholdFailed'))
    }
  }, [])

  const toggleDnd = useCallback(async () => {
    const next = !dndRef.current
    updateDnd(next) // optimistic
    const seq = ++dndRequestSeqRef.current
    try {
      const confirmed = await callApi.setDnd(next)
      if (mountedRef.current && seq === dndRequestSeqRef.current) updateDnd(confirmed)
    } catch (err) {
      console.warn('[useCallSocket] toggleDnd failed, reverting', err)
      if (mountedRef.current && seq === dndRequestSeqRef.current) updateDnd(!next)
      toast.error(i18n.t('callRouting:callStatus.dndUpdateFailed'))
    }
  }, [updateDnd])

  useEffect(() => {
    // 未启用（无权限/租户未配电话）：什么都不启动。上一次 enabled=true 的
    // cleanup 已负责拆除连接，这里直接返回即可。
    if (!enabled) return

    mountedRef.current = true
    stoppedRef.current = false
    // 每次启动重置 403 闸门：同一页内换账号（新账号有 call:read）时,
    // 上一个账号留下的 forbidden 标记会让 active calls 永不再同步。
    activeCallsForbiddenRef.current = false

    // 统一登出（含 401 auth_user_inactive 终态）后彻底停机：关闭连接、
    // 取消已排期的重连，不再重试
    const handleUnauthorized = () => {
      stoppedRef.current = true
      if (tokenRefreshTimerRef.current) clearTimeout(tokenRefreshTimerRef.current)
      tokenRefreshTimerRef.current = null
      if (twilioDeviceRef.current) {
        try { twilioDeviceRef.current.destroy() } catch {}
        twilioDeviceRef.current = null
      }
      tokenRefreshAbortRef.current?.abort()
      tokenRefreshInFlightRef.current = null
      resetDeviceNotice()
      epochRef.current += 1
      reseedAbortRef.current?.abort()
      if (reseedRetryRef.current) clearTimeout(reseedRetryRef.current)
      reseedRetryRef.current = null
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      clearWsTimers()
      wsRef.current?.close()
      wsRef.current = null
      setConnectionStatus('disconnected')
    }
    window.addEventListener('auth:unauthorized', handleUnauthorized)

    if (WS_BASE && navigator.onLine) {
      // Same reconcile path as every reconnect (see reconcileActiveCalls): a WS
      // incoming_call may already have added an entry while this REST seed is in
      // flight, and anything added at/after the seed's issue time survives even
      // though the snapshot predates it. prev is always [] this early, so there
      // is nothing to drop yet — this just keeps mount and reconnect identical.
      resyncActiveCalls()
      const dndRequest = dndRequestSeqRef.current
      void callApi.getDnd().then((val) => {
        if (mountedRef.current && dndRequest === dndRequestSeqRef.current) updateDnd(val)
      }).catch(() => {})
      void connect()
      void initTwilioDevice()
    }

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized)
      mountedRef.current = false
      tokenRefreshAbortRef.current?.abort()
      tokenRefreshInFlightRef.current = null
      // Increment epoch BEFORE closing so the onclose handler sees a stale epoch
      // and does not schedule a reconnect (handles StrictMode double-invoke and
      // normal unmount equally).
      epochRef.current += 1
      reseedAbortRef.current?.abort()
      if (reseedRetryRef.current) clearTimeout(reseedRetryRef.current)
      reseedRetryRef.current = null
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      if (tokenRefreshTimerRef.current) clearTimeout(tokenRefreshTimerRef.current)
      tokenRefreshTimerRef.current = null
      if (deviceLossTimerRef.current) clearTimeout(deviceLossTimerRef.current)
      deviceLossTimerRef.current = null
      if (deviceNoticeShownRef.current) {
        deviceNoticeShownRef.current = false
        toast.dismiss(DEVICE_NOTICE_TOAST_ID)
      }
      clearWsTimers()
      wsRef.current?.close()
      wsRef.current = null
      if (twilioDeviceRef.current) {
        try { twilioDeviceRef.current.destroy() } catch {}
        twilioDeviceRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  return {
    incomingCalls,
    activeCall,
    activeCalls,
    dnd,
    connectionStatus,
    answerCall,
    declineCall,
    endCall,
    holdCall,
    unholdCall,
    toggleDnd,
    deviceStatus,
  }
}
